/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file A tag that highlights details about an object shown in a widget
 * @module Epic.AppOrchard.Dashboard.WidgetTag
 */

import { IconProps, MergeWithAs, Tag, TagLabel, TagLeftIcon, TagProps, TagRightIcon } from "@chakra-ui/react";
import { AOTooltip } from "ao/components/Core";
import { ITooltipWithoutChildrenProps } from "ao/components/Core/AOTooltip";
import React, { FC, memo } from "react";
type IconPropsType = MergeWithAs<React.ComponentProps<"svg">, React.ComponentProps<"svg">, IconProps, "svg">;

interface IProps {
	/** text label shown in the tag */
	label: React.ReactNode;
	/** if passed, the tag will be a trigger for a tooltip with this content */
	tooltipContent?: React.ReactNode;
	tooltipProps?: ITooltipWithoutChildrenProps;
	/** icon to show to the left of the tag label */
	icon?: any;
	/** icon to show to the right of the tag label */
	rightIcon?: any;
	/** props for the icon */
	iconProps?: IconPropsType;
	/** props for the right icon */
	rightIconProps?: IconPropsType;
}

type Props = IProps & TagProps;

/** A tag that highlights details about an object shown in a widget and optionally shows helptext on hover/click, etc */
export const WidgetTag: FC<Props> = memo((props: Props) => {
	const {
		tooltipContent,
		label,
		icon,
		iconProps,
		tooltipProps,
		rightIcon,
		rightIconProps,
		...rest
	} = props;
	const tag = (
		<Tag {...rest} color="aoGreen.foreground" background="aoGreen.background">
			{icon && <TagLeftIcon as={icon} {...iconProps} />}
			<TagLabel>{label}</TagLabel>
			{rightIcon && <TagRightIcon as={rightIcon} {...rightIconProps} />}
		</Tag>
	);
	return tooltipContent ? (
		<AOTooltip
			helptext={tooltipContent}
			{...tooltipProps}
			boxProps={{ display: "inline", width: "100%" }}
		>
			{tag}
		</AOTooltip>
	) : (
		tag
	);
});
